import { AlertDialogTrigger } from '@radix-ui/react-alert-dialog';
import { useSelector } from '@xstate/react';
import { useFormContext } from 'react-hook-form';

import { TooltipLabel } from '@eluve/blocks';
import {
  FormControl,
  FormField,
  FormItem,
  HStack,
  Input,
  NewButton,
  cn,
} from '@eluve/components';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Icon,
} from '@eluve/components';

import { BlockTypeIcon } from './BlockTypeIcon';
import { templateBuilderStore } from './templateBuilderStore';
import { FlattenedBlock } from './utilities';

const LabelPlaceholder: Partial<Record<FlattenedBlock['type'], string>> = {
  group: 'Group name',
  text: 'Text block name',
  checkbox: 'Checkbox name',
};

interface TreeItemHeaderProps {
  handleProps?: React.HTMLAttributes<HTMLDivElement>;
  collapsed?: boolean;
  block: FlattenedBlock;
}

export const TreeItemHeader: React.FC<TreeItemHeaderProps> = ({
  handleProps,
  collapsed,
  block,
}) => {
  const { control } = useFormContext();

  const isReadonly = useSelector(
    templateBuilderStore,
    (store) => store.context.isReadOnly,
  );

  const isImported = useSelector(
    templateBuilderStore,
    (store) => store.context.isImported,
  );

  return (
    <HStack className="border-b bg-bg-secondary p-5" justify="between" gap={4}>
      <HStack gap={4}>
        {!isReadonly && !isImported && (
          <div {...handleProps}>
            <Icon name="GripVertical" size="xs" />
          </div>
        )}
        <TooltipLabel label={collapsed ? 'Expand' : 'Collapse'}>
          <NewButton
            icon={{
              name: !collapsed ? 'ChevronsDownUp' : 'ChevronsUpDown',
            }}
            type="outline"
            onClick={() =>
              templateBuilderStore.send({
                type: 'toggleCollapse',
                id: block.id,
                collapsed: !collapsed,
              })
            }
          />
        </TooltipLabel>
        <BlockTypeIcon type={block.type} />
        <FormField
          control={control}
          name="label"
          render={({ field, fieldState: { error } }) => (
            <FormItem className="flex-1">
              <FormControl>
                <Input
                  placeholder={LabelPlaceholder[block.type]}
                  {...field}
                  disabled={isReadonly || isImported}
                  className={cn(error && 'border-border-negative-strong')}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </HStack>
      {!isReadonly && !isImported && (
        <AlertDialog>
          <TooltipLabel label="Remove block">
            <AlertDialogTrigger asChild>
              <NewButton type="outline" icon={{ name: 'Trash' }} />
            </AlertDialogTrigger>
          </TooltipLabel>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                Removing this block cannot be reversed.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                buttonProps={{ variant: 'destructive' }}
                onClick={() =>
                  templateBuilderStore.send({
                    type: 'removeBlock',
                    id: block.id,
                  })
                }
              >
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </HStack>
  );
};
